// EditUserKpiDialog.jsx - Dialog for editing User KPI entity data

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Chip,
    Box,
    CircularProgress,
    Alert,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const EditUserKpiDialog = () => {
    const dispatch = useDispatch();
    
    // Selectors
    const open = useSelector(state => state.userKpi.dialogs.editUserKpiDialog);
    const userKpi = useSelector(state => state.userKpi.dialogData.userKpi);
    const loading = useSelector(selectors.selectLoading);
    const allUsers = useSelector(selectors.selectUsersForEdit) || []; // Use separate list for edit dialog
    const allDesignations = useSelector(selectors.selectAllDesignations) || [];
    
    // Form state
    const [formData, setFormData] = useState({
        designationAtAssignment: '',
        rolesAtAssignment: [],
        assignedByEmployee: '',
        directReportsAtAssignment: []
    });

    // Initialize form data when dialog opens
    useEffect(() => {
        if (open && userKpi) {
            setFormData({
                designationAtAssignment: userKpi.designationAtAssignment?.name || userKpi.designationAtAssignment || '',
                rolesAtAssignment: Array.isArray(userKpi.rolesAtAssignment) 
                    ? userKpi.rolesAtAssignment 
                    : [],
                assignedByEmployee: userKpi.assignedByEmployee || '',
                directReportsAtAssignment: Array.isArray(userKpi.directReportsAtAssignment)
                    ? userKpi.directReportsAtAssignment
                    : []
            });
            
            // Fetch users if not already loaded
            if (allUsers.length === 0) {
                dispatch(actions.fetchUsersForEditRequest()); // Use separate action for edit dialog
            }
            
            // Fetch designations if not already loaded
            if (allDesignations.length === 0) {
                dispatch(actions.fetchAllDesignationsRequest());
            }
        }
    }, [open, userKpi, dispatch, allUsers.length, allDesignations.length]);

    const handleClose = () => {
        dispatch(actions.closeEditUserKpiDialog());
        // Reset form
        setFormData({
            designationAtAssignment: '',
            rolesAtAssignment: [],
            assignedByEmployee: '',
            directReportsAtAssignment: []
        });
    };

    const handleSubmit = () => {
        if (!userKpi || !userKpi.id) return;
        
        dispatch(actions.updateUserKpiRequest(userKpi.id, formData));
    };

    const handleRoleInput = (event, value) => {
        setFormData(prev => ({
            ...prev,
            rolesAtAssignment: value
        }));
    };

    const handleDirectReportsChange = (event, value) => {
        setFormData(prev => ({
            ...prev,
            directReportsAtAssignment: value
        }));
    };

    // Get user options (email list)
    const userOptions = allUsers
        .map(user => user.user?.email || user.employeeId)
        .filter(Boolean);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Edit User KPI - {userKpi?.userId}
            </DialogTitle>
                <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {/* Warning Alert */}
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        <strong>Historical Snapshot:</strong> These values represent the user's position, 
                        roles, and organizational structure at the time this KPI was assigned. 
                        They are used for reference and reporting purposes for this specific period.
                    </Alert>

                    {/* Designation */}
                    <Autocomplete
                        options={allDesignations}
                        getOptionLabel={(option) => option.name || ''}
                        value={allDesignations.find(d => d.name === formData.designationAtAssignment) || null}
                        onChange={(e, newValue) => setFormData(prev => ({
                            ...prev,
                            designationAtAssignment: newValue ? newValue.name : ''
                        }))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Designation at Assignment"
                                placeholder="Select designation"
                                fullWidth
                            />
                        )}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="body2">{option.name}</Typography>
                                    {option.description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {option.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    />

                    {/* Roles */}
                    <Autocomplete
                        sx={{ display: 'none' }}
                        multiple
                        freeSolo
                        options={['admin', 'employee', 'manager', 'hr']}
                        value={formData.rolesAtAssignment}
                        onChange={handleRoleInput}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={option}
                                    {...getTagProps({ index })}
                                    key={option}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Roles at Assignment"
                                placeholder="Type and press Enter to add roles"
                                helperText="Common roles: admin, employee, manager, hr"
                            />
                        )}
                    />

                    {/* Assigned By Employee */}
                    <Autocomplete
                        freeSolo
                        options={userOptions}
                        value={formData.assignedByEmployee}
                        onChange={(event, newValue) => {
                            setFormData(prev => ({
                                ...prev,
                                assignedByEmployee: newValue || ''
                            }));
                        }}
                        onInputChange={(event, newInputValue) => {
                            setFormData(prev => ({
                                ...prev,
                                assignedByEmployee: newInputValue
                            }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Assigned By Employee"
                                placeholder="Select or enter email"
                                helperText="Email of the employee who assigned this KPI"
                            />
                        )}
                    />

                    {/* Direct Reports */}
                    <Autocomplete
                        multiple
                        freeSolo
                        options={userOptions}
                        value={formData.directReportsAtAssignment}
                        onChange={handleDirectReportsChange}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={option}
                                    {...getTagProps({ index })}
                                    key={`${option}-${index}`}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Direct Reports at Assignment"
                                placeholder="Select employees or enter emails"
                                helperText="Emails of direct reports at the time of assignment"
                            />
                        )}
                    />

                    {/* Info Box */}
                    <Box 
                        sx={{ 
                            p: 2, 
                            bgcolor: (theme) => alpha(theme.palette.divider, 0.06), 
                            borderRadius: 1,
                            fontSize: '0.875rem'
                        }}
                    >
                        <strong>Note:</strong> This updates the User KPI entity data only. 
                        To manage goals, use the "Manage Goals" action.
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {loading ? 'Updating...' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditUserKpiDialog;
